import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BulkSelectRolesComponent } from '@app/components';
import { FeatureFlag } from '@app/enums';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { BaseForm } from '@forms/base.form';
import { Role } from '@models/account/role.model';
import { TranslateService } from '@ngx-translate/core';
import { Profile } from '@settings/interfaces';
import { Module, ModuleFeature } from '@settings/interfaces/module-permissions.interface';
import { ManageRolesAndPermissions } from '@settings/meta/module-feature-permissions.meta';
import { GetRolesFor } from './get-roles-for-company.service';

@Component({
    selector: 'app-form-role-modules',
    templateUrl: './role-modules.template.html',
    styleUrls: ['./role-modules.style.scss'],
    providers: [GetRolesFor],
})
export class RoleModulesForm extends BaseForm implements OnInit {
    @ViewChild('selectRoles', { static: true }) selectRoles: BulkSelectRolesComponent;

    @Input() role: Role;
    @Input() profilePermissions: Profile[];
    @Input() moduleFeaturePermissions: Module[];
    @Input() assignableRoles: Role[];

    @Output() assignableRolesChange: EventEmitter<Role[]> = new EventEmitter<Role[]>();

    roleTotal: number | null = null;

    showFeatureFlagMap: Map<FeatureFlag, boolean> = new Map<FeatureFlag, boolean>();
    hideFeatureFlagMap: Map<FeatureFlag, boolean> = new Map<FeatureFlag, boolean>();

    currentUserCanManageRoles = false;
    roleBulkSelectDisabled = false;

    constructor(
        protected featureService: FeatureService,
        protected changeDetector: ChangeDetectorRef,
        protected auth: AuthService,
        private readonly getRolesFor: GetRolesFor,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit(): void {
        this.currentUserCanManageRoles = this.auth.can(ManageRolesAndPermissions.permissions);
        this.getRolesFor.company(this.auth.company?.id).then(([roles]) => (this.roleTotal = roles.length));
    }

    displayName(val: string[] = []): string {
        return val.map((translationStr) => this.translate.instant(translationStr)).join('-');
    }

    /**
     *
     * This function was previously using un-translated strings for modules
     * and using them in the format of `Navigation.Administrator View` as an example for the module.dependency prop.
     * This would then be split into an array of ['Navigation', 'Administrator View']
     * then it would try to compare them to translation strings which would result in the toggle being
     * disabled. Now the function by default will using an array of strings which will be translation string
     * dependency: [
            'settings.moduleFeaturePermissions.navigation', // module name
            'settings.moduleFeaturePermissions.administratorView', // feature label
        ],
     */
    moduleDependencyMet(module: Module): boolean {
        // Set disabled property for module features in every module
        this.setDefaultDisabledPropertyForModuleFeatures(module);

        const dependency = module.dependency;

        if (!dependency) {
            return true;
        }

        const args = dependency;

        if (args.length > 1) {
            const [moduleName, featureLabel] = args;
            const dependencyModule = this.moduleFeaturePermissions.find((m: Module) => {
                return m.title === moduleName;
            });

            if (!dependencyModule) {
                return false;
            }

            const dependencyToggled =
                dependencyModule.features.find((f: ModuleFeature) => {
                    return f.label === featureLabel;
                }).toggled || false;

            // If the dependency is toggled off, the module must be toggled off as well.
            if (!dependencyToggled) {
                module.toggled = false;
            }

            return dependencyToggled;
        }

        const dependencyToggled =
            this.moduleFeaturePermissions.find((m: Module) => {
                return m.title === module.dependency[0];
            }).toggled || false;

        // If the dependency is toggled off, the module must be toggled off as well.
        if (!dependencyToggled) {
            module.toggled = false;
        }

        return dependencyToggled;
    }

    /**
     * Toggle off a module's feature's permissions
     */
    onToggleModule(module: Module): void {
        const { toggled } = module;
        if (!toggled) {
            module.features.forEach((feature: ModuleFeature) => {
                feature.toggled = false;
            });
        }
        this.cascadeDisableTab(module);
    }

    /**
     * The following method will disable the dependent feature when the current feature is toggled off
     * and enable it when the current feature is toggled back on.
     */
    disableDependentFeatureOnToggleOff(feature: ModuleFeature): void {
        if (!feature.disableDependentOnToggleOff) {
            return;
        }

        const dependentFeature = this.getDependentFeature(feature);

        if (feature.toggled) {
            dependentFeature.isDisabled = false; // Reset back to enable
            return;
        }

        // If the current feature if toggled off, dependent feature must be disabled and toggled off as well.
        dependentFeature.toggled = false;
        dependentFeature.isDisabled = true;
    }

    /**
     * If a module has a cascadeDisableTab, the profile tab should have its segments disabled by default
     * when the module is disabled. This is required as the module and segment may share permissions which
     * will ignore the user input and re-enable the module.
     */
    cascadeDisableTab(module: Module): void {
        if (module.cascadeDisableTab) {
            this.profilePermissions
                .find((profilePermission) => profilePermission.tab === module.cascadeDisableTab)
                ?.segments.forEach((segment) => (segment.level = null));
        }
    }

    moduleIsToggleable(module: Module): boolean {
        return 'toggled' in module;
    }

    openAssignableModal(): void {
        this.roleBulkSelectDisabled = this.role.isImmutable;
        this.selectRoles
            // if current user can manage roles, do not limit to its own role
            .assignableBy(this.currentUserCanManageRoles ? null : this.auth.role)
            .show(this.assignableRoles)
            .then((roles: Role[] | null) => {
                this.roleBulkSelectDisabled = false;
                if (roles) {
                    this.assignableRolesChange.emit(roles);
                }
            });
    }

    /**
     * The following method can be used to set disabled property for module features.
     * For now, it's set based on the disableDependent property but it can be used
     * on other properties in future as well.
     */
    private setDefaultDisabledPropertyForModuleFeatures(module: Module): void {
        module.features.forEach((moduleFeature) => {
            if (moduleFeature.disableDependentOnToggleOff) {
                this.disableDependentFeatureOnToggleOff(moduleFeature);
            }
        });
    }

    private getDependentFeature(feature: ModuleFeature): ModuleFeature | null {
        let dependencyFeature = null;

        for (let i = 0; i < this.moduleFeaturePermissions.length; i++) {
            const currentModule = this.moduleFeaturePermissions[i];

            dependencyFeature = currentModule.features.find((moduleFeature) => {
                return moduleFeature.label === feature.disableDependentOnToggleOff;
            });

            if (dependencyFeature) {
                break;
            }
        }

        return dependencyFeature;
    }
}
